"use client";

import { Container } from "@/components/ui/container";
import { TitlePage } from "@/components/ui/title-page";
import { PrivacyIllustration } from "./_components/privacy-illustration";
import Link from "next/link";
import { motion, type Variants } from "framer-motion";
import { Icon } from "@/components/ui/icon";
import { Alert02Icon, Building03Icon, Call02Icon, CheckmarkBadge01Icon, Clock01Icon, Comment01Icon, CookieIcon, Database01Icon, File01Icon, Globe02Icon, Location01Icon, Mail01Icon, RefreshIcon, ShieldUserIcon, SquareLock01Icon, Target01Icon, UserGroupIcon, WeightScaleIcon } from "@hugeicons/core-free-icons";

const section1 = {
  icon: File01Icon,
  number: "1",
  title: "Общие положения",
  items: [
    "Настоящая Политика конфиденциальности разработана в соответствии с Законом Республики Беларусь от 07.05.2021 № 99-З «О защите персональных данных».",
    "Используя Сайт, пользователь подтверждает своё согласие с настоящей Политикой и условиями обработки его персональных данных.",
    "В случае несогласия с условиями Политики пользователь должен прекратить использование Сайта.",
    "Оператор обеспечивает защиту персональных данных от несанкционированного доступа, изменения, раскрытия или уничтожения.",
  ],
};

const sections = [
  {
    icon: Database01Icon,
    number: "3",
    title: "Источники и категории обрабатываемых данных",
    items: [
      "Персональные данные предоставляются пользователем самостоятельно при оформлении заказа или заявки через формы на Сайте.",
      "Оператор обрабатывает следующие данные: имя; номер телефона; адрес доставки (город, улица, дом, квартира, этаж); мессенджер для связи; комментарии к заказу.",
      "Автоматически могут собираться технические данные: IP-адрес; тип и версия браузера; тип устройства; сведения о посещённых страницах Сайта; файлы cookies.",
      "Оператор не обрабатывает специальные категории персональных данных (сведения о здоровье, биометрические данные, данные о судимостях).",
    ],
  },
  {
    icon: Target01Icon,
    number: "4",
    title: "Цели обработки персональных данных",
    items: [
      "Оформление, подтверждение и выполнение заказов на доставку питания.",
      "Организация доставки по указанному адресу.",
      "Связь с пользователем по вопросам заказа, изменения адреса или состава заказа.",
      "Уведомление пользователя о статусе заказа через мессенджеры или телефон.",
      "Улучшение качества работы Сайта и сервиса; анализ посещаемости (при наличии согласия).",
    ],
  },
  {
    icon: WeightScaleIcon,
    number: "5",
    title: "Правовые основания обработки",
    items: [
      "Согласие субъекта персональных данных (ст. 6 Закона № 99-З) — для обработки данных при оформлении заказа.",
      "Необходимость исполнения договора, стороной которого является субъект персональных данных (оформление и выполнение заказа на доставку).",
      "Иные основания, предусмотренные законодательством Республики Беларусь.",
    ],
  },
  {
    icon: Globe02Icon,
    number: "6",
    title: "Трансграничная передача персональных данных",
    items: [
      "Для хранения и обработки персональных данных Оператор использует инфраструктуру облачных сервисов, серверы которых могут располагаться за пределами Республики Беларусь.",
      "Передача данных осуществляется в страны, обеспечивающие надлежащий уровень защиты персональных данных в соответствии с Приказом НЦЗПД № 14 от 15.11.2021 (в ред. Приказа № 114 от 26.12.2022), — в частности, в государства — члены Конвенции Совета Европы о защите физических лиц в отношении автоматизированной обработки данных личного характера.",
      "При наличии серверов в странах, не включённых в перечень стран с надлежащей защитой (например, США), передача осуществляется исключительно на основании согласия субъекта, осведомлённого о возможных рисках, связанных с отсутствием в таком государстве норм защиты, аналогичных белорусским.",
      "Пользователь, оформляя заказ на Сайте, подтверждает согласие с возможной передачей его данных в указанных условиях.",
    ],
  },
  {
    icon: CookieIcon,
    number: "7",
    title: "Cookies и аналитика",
    items: [
      "Сайт использует cookies для обеспечения корректной работы и улучшения пользовательского опыта.",
      "На Сайте может использоваться сервис веб-аналитики для сбора обезличенной информации о взаимодействии пользователей с Сайтом.",
      "Аналитические cookies используются только при согласии пользователя.",
      "Пользователь может ограничить или отключить использование cookies в настройках своего браузера.",
    ],
  },
  {
    icon: UserGroupIcon,
    number: "8",
    title: "Передача персональных данных третьим лицам",
    items: [
      "Оператор не передаёт персональные данные третьим лицам, за исключением: курьерских и логистических служб — для выполнения доставки; технических подрядчиков (хостинг, облачная инфраструктура) — для обеспечения работы Сайта; сервисов аналитики — при наличии согласия пользователя.",
      "Передача данных государственным органам осуществляется в случаях, прямо предусмотренных законодательством Республики Беларусь.",
      "Передача данных осуществляется в объёме, необходимом для достижения целей обработки.",
    ],
  },
  {
    icon: Clock01Icon,
    number: "9",
    title: "Сроки хранения персональных данных",
    items: [
      "Данные клиента (имя, телефон, адрес доставки, история заказов) хранятся в течение 3 (трёх) лет с момента последнего заказа или до отзыва согласия на обработку.",
      "Согласия субъектов персональных данных хранятся в течение 1 (одного) года после истечения срока, на который они были предоставлены (Постановление Министерства юстиции РБ № 1 от 11.01.2023).",
      "Технические данные (логи, cookies) хранятся не более 12 месяцев.",
      "После истечения сроков данные подлежат удалению или обезличиванию.",
    ],
  },
  {
    icon: CheckmarkBadge01Icon,
    number: "10",
    title: "Права субъекта персональных данных",
    items: [
      "Получать информацию о своих персональных данных и целях их обработки.",
      "Требовать внесения изменений (исправления) в свои персональные данные.",
      "Отзывать согласие на обработку персональных данных в любое время.",
      "Требовать прекращения обработки и удаления своих персональных данных.",
      "Обжаловать действия Оператора в Национальный центр защиты персональных данных Республики Беларусь (cpd.by).",
      "Для реализации своих прав пользователь направляет письменный запрос на электронную почту Оператора: okeygood@gmail.com. Оператор отвечает в срок не более 15 (пятнадцати) рабочих дней.",
    ],
  },
  {
    icon: SquareLock01Icon,
    number: "11",
    title: "Меры по защите персональных данных",
    items: [
      "Оператор принимает необходимые правовые, организационные и технические меры для защиты персональных данных от неправомерного или случайного доступа, изменения, раскрытия и уничтожения.",
      "Назначено ответственное лицо за осуществление внутреннего контроля за обработкой персональных данных.",
      "Доступ к персональным данным имеют только сотрудники, которым это необходимо для исполнения должностных обязанностей.",
    ],
  },
  {
    icon: Alert02Icon,
    number: "12",
    title: "Уведомление об инцидентах",
    items: [
      "В случае выявления несанкционированного доступа к персональным данным или иного нарушения системы их защиты Оператор принимает незамедлительные меры по устранению последствий.",
      "Пользователи, чьи данные могли быть затронуты инцидентом, уведомляются в разумные сроки по доступным контактным данным.",
      "Оператор взаимодействует с НЦЗПД в порядке, установленном законодательством Республики Беларусь.",
    ],
  },
  {
    icon: RefreshIcon,
    number: "13",
    title: "Изменения в Политике конфиденциальности",
    items: [
      "Оператор вправе вносить изменения в настоящую Политику. Актуальная версия всегда доступна на данной странице.",
      "При существенных изменениях Оператор уведомляет пользователей путём публикации новой редакции на Сайте с указанием даты обновления.",
    ],
  },
  {
    icon: Comment01Icon,
    number: "14",
    title: "Заключительные положения",
    items: [
      "Все вопросы, связанные с обработкой персональных данных, пользователь может направить по контактным данным, указанным в разделе 2 настоящей Политики.",
      "Надзор в сфере защиты персональных данных в Республике Беларусь осуществляет Национальный центр защиты персональных данных (cpd.by, тел. 115).",
    ],
  },
];

const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

export default function PrivacyPage() {
  return (
    <>
      <TitlePage
        title="Политика конфиденциальности"
        description="Мы объясняем, какие данные собираем при оформлении заказа, зачем они нужны, как передаются и как вы можете управлять своими данными."
        updated="02.04.2026"
        illustration={<PrivacyIllustration className="h-[150px] w-[150px] md:h-[220px] md:w-[220px] lg:h-[260px] lg:w-[260px]" />}
      />

      <div className="bg-whitePrimary pb-20">
        <Container>
          <div className="mx-auto max-w-3xl space-y-6">

            {/* Section 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="group rounded-2xl border border-greySecondary/10 bg-white p-6 shadow-sm"
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-colorPrimary/8 text-colorPrimary">
                  <Icon icon={section1.icon} className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-yellow-hover">Раздел {section1.number}</p>
                  <h2 className="mt-0.5 text-[17px] font-extrabold leading-snug text-colorPrimary">{section1.title}</h2>
                </div>
              </div>
              <ul className="space-y-2.5">
                {section1.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-hover" />
                    <p className="text-sm leading-relaxed text-greySecondary">{item}</p>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Operator card — Section 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="rounded-2xl border border-colorPrimary/12 bg-white p-6 shadow-sm"
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-colorPrimary text-white shadow-sm">
                  <Icon icon={Building03Icon} className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-yellow-hover">
                    Раздел 2
                  </p>
                  <h2 className="mt-0.5 text-[17px] font-extrabold leading-snug text-colorPrimary">
                    Оператор персональных данных
                  </h2>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex items-start gap-2.5 rounded-xl bg-whitePrimary p-3">
                  <Icon icon={Building03Icon} className="mt-0.5 h-4 w-4 shrink-0 text-greySecondary" />
                  <div>
                    <p className="text-xs text-greySecondary">Наименование</p>
                    <p className="text-sm font-medium text-colorPrimary">ИП / ООО Okey Food</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5 rounded-xl bg-whitePrimary p-3">
                  <Icon icon={Location01Icon} className="mt-0.5 h-4 w-4 shrink-0 text-greySecondary" />
                  <div>
                    <p className="text-xs text-greySecondary">Адрес регистрации</p>
                    <p className="text-sm font-medium text-colorPrimary">г. Минск, пр. Дзержинского 10</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5 rounded-xl bg-whitePrimary p-3">
                  <Icon icon={Mail01Icon} className="mt-0.5 h-4 w-4 shrink-0 text-greySecondary" />
                  <div>
                    <p className="text-xs text-greySecondary">Email (ответственное лицо)</p>
                    <Link
                      href="mailto:okeygood@gmail.com"
                      className="text-sm font-medium text-colorPrimary underline-offset-2 hover:underline"
                    >
                      okeygood@gmail.com
                    </Link>
                  </div>
                </div>
                <div className="flex items-start gap-2.5 rounded-xl bg-whitePrimary p-3">
                  <Icon icon={Call02Icon} className="mt-0.5 h-4 w-4 shrink-0 text-greySecondary" />
                  <div>
                    <p className="text-xs text-greySecondary">Телефон</p>
                    <Link
                      href="tel:+375336237885"
                      className="text-sm font-medium text-colorPrimary underline-offset-2 hover:underline"
                    >
                      +375 (33) 623-78-85
                    </Link>
                  </div>
                </div>
              </div>

              <p className="mt-3 text-xs leading-relaxed text-greySecondary">
                По указанным контактам пользователь может обратиться по вопросам обработки персональных данных.
                Ответ предоставляется в срок не более <span className="font-medium text-colorPrimary">15 рабочих дней</span>.
              </p>
            </motion.div>

            {/* Sections */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-4"
            >
              {sections.map((section) => {
                
                return (
                  <motion.div
                    key={section.number}
                    variants={itemVariants}
                    className="group rounded-2xl border border-greySecondary/10 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md"
                  >
                    <div className="mb-4 flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-colorPrimary/8 text-colorPrimary transition-colors duration-200 group-hover:bg-colorPrimary group-hover:text-white">
                        <Icon icon={section.icon} className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-yellow-hover">
                          Раздел {section.number}
                        </p>
                        <h2 className="mt-0.5 text-[17px] font-extrabold leading-snug text-colorPrimary">
                          {section.title}
                        </h2>
                      </div>
                    </div>

                    <ul className="space-y-2.5">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-hover" />
                          <p className="text-sm leading-relaxed text-greySecondary">{item}</p>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Cross-link to offer */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.45 }}
              className="flex items-center justify-between rounded-xl border border-greySecondary/10 bg-white px-5 py-3.5"
            >
              <p className="text-sm text-greySecondary">
                Ознакомьтесь также с нашей{" "}
                <Link href="/offer" className="font-medium text-colorPrimary underline-offset-2 hover:underline">
                  Публичной офертой
                </Link>
              </p>
            </motion.div>

            {/* Footer note */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              className="flex flex-col gap-2 rounded-xl border border-greySecondary/10 bg-white px-5 py-4 text-sm text-greySecondary sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-center gap-2">
                <Icon icon={ShieldUserIcon} className="h-4 w-4 shrink-0 text-colorPrimary" />
                <span>Документ соответствует Закону РБ № 99-З от 07.05.2021</span>
              </div>
              <div className="flex items-center gap-3 sm:shrink-0">
                <span className="text-xs">Надзорный орган:</span>
                <Link
                  href="https://cpd.by"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-colorPrimary underline-offset-2 hover:underline"
                >
                  cpd.by
                </Link>
                <span className="font-medium text-colorPrimary">02.04.2026</span>
              </div>
            </motion.div>

          </div>
        </Container>
      </div>
    </>
  );
}
